import { useEffect, useState } from 'react';
import { useTerritoryStore } from '../../../store/territoryStore';
import type { RegionCreate, RegionUpdate } from '../../../types';

interface RegionFormProps {
  regionId?: string;
  onClose: () => void;
}

const DEFAULT_COLORS = [
  '#3B82F6', // blue
  '#10B981', // green
  '#F59E0B', // amber
  '#EF4444', // red
  '#8B5CF6', // purple
  '#EC4899', // pink
  '#06B6D4', // cyan
  '#F97316', // orange
];

export default function RegionForm({ regionId, onClose }: RegionFormProps) {
  const { regions, createRegion, updateRegion } = useTerritoryStore();

  const existing = regionId ? regions.find((r) => r.id === regionId) : undefined;

  const [name, setName] = useState(existing?.name ?? '');
  const [code, setCode] = useState(existing?.code ?? '');
  const [color, setColor] = useState(existing?.color ?? DEFAULT_COLORS[0]);
  const [description, setDescription] = useState(existing?.description ?? '');
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Sync if regionId changes (e.g. selecting different region)
  useEffect(() => {
    if (existing) {
      setName(existing.name);
      setCode(existing.code);
      setColor(existing.color);
      setDescription(existing.description ?? '');
    } else {
      setName('');
      setCode('');
      setColor(DEFAULT_COLORS[0]);
      setDescription('');
    }
  }, [regionId]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleCodeChange = (val: string) => {
    setCode(val.toUpperCase().slice(0, 4));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!name.trim()) return setError('Name is required.');
    if (!code.trim() || code.length < 2) return setError('Code must be at least 2 characters.');
    if (!color) return setError('Color is required.');

    setIsSaving(true);
    try {
      if (regionId) {
        const update: RegionUpdate = { name: name.trim(), code, color, description: description.trim() || undefined };
        await updateRegion(regionId, update);
      } else {
        const create: RegionCreate = { name: name.trim(), code, color, description: description.trim() || undefined };
        await createRegion(create);
      }
      onClose();
    } catch (err: any) {
      setError(err.response?.data?.message ?? 'Failed to save region.');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-5">
      <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100 mb-4">
        {regionId ? 'Edit Region' : 'New Region'}
      </h3>

      <form onSubmit={handleSubmit} className="space-y-4">
        {error && (
          <p className="text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded px-3 py-2">
            {error}
          </p>
        )}

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Name <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Northeast"
            className="w-full border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 dark:placeholder-gray-400 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Code <span className="text-red-500">*</span>
            <span className="text-gray-400 dark:text-gray-500 font-normal ml-1">(up to 4 uppercase chars)</span>
          </label>
          <input
            type="text"
            value={code}
            onChange={(e) => handleCodeChange(e.target.value)}
            placeholder="e.g. NE"
            maxLength={4}
            className="w-full border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 dark:placeholder-gray-400 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 uppercase"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Color <span className="text-red-500">*</span>
          </label>
          <div className="flex items-center gap-3 flex-wrap">
            {DEFAULT_COLORS.map((c) => (
              <button
                key={c}
                type="button"
                onClick={() => setColor(c)}
                className={`w-7 h-7 rounded-full border-2 transition-transform ${
                  color === c ? 'border-gray-900 dark:border-gray-100 scale-110' : 'border-transparent hover:scale-105'
                }`}
                style={{ backgroundColor: c }}
                aria-label={c}
              />
            ))}
            <div className="flex items-center gap-2 ml-1">
              <input
                type="color"
                value={color}
                onChange={(e) => setColor(e.target.value)}
                className="w-7 h-7 rounded cursor-pointer border border-gray-300 dark:border-gray-600"
                title="Custom color"
              />
              <span className="text-xs text-gray-500 dark:text-gray-400 font-mono">{color}</span>
            </div>
          </div>
          <div
            className="mt-2 w-8 h-8 rounded-md border border-gray-200 dark:border-gray-700"
            style={{ backgroundColor: color }}
            title="Preview"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Description</label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={3}
            placeholder="Optional description..."
            className="w-full border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 dark:placeholder-gray-400 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
          />
        </div>

        <div className="flex justify-end gap-3 pt-1">
          <button
            type="button"
            onClick={onClose}
            className="border border-gray-300 dark:border-gray-600 px-4 py-2 rounded-md text-sm hover:bg-gray-50 dark:hover:bg-gray-700 dark:text-gray-300"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isSaving}
            className="bg-blue-600 text-white px-4 py-2 rounded-md text-sm hover:bg-blue-700 disabled:opacity-60"
          >
            {isSaving ? 'Saving...' : regionId ? 'Save Changes' : 'Create Region'}
          </button>
        </div>
      </form>
    </div>
  );
}
